import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  [
    // Base styles
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium",
    // Transitions - all properties for smooth animations
    "transition-all duration-200 ease-out",
    // Focus states - enhanced accessibility
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rsb-accent focus-visible:ring-offset-2 focus-visible:ring-offset-rsb-bg",
    // Disabled states
    "disabled:pointer-events-none disabled:opacity-50",
    // Icon sizing
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    // Press feedback - subtle scale on active
    "active:scale-[0.98] active:transition-none",
    // Hover scale
    "hover:scale-[1.02]"
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "bg-rsb-accent text-white shadow-lg shadow-rsb-accent/20",
          "hover:bg-rsb-accent/90 hover:shadow-rsb-accent/30",
          "active:shadow-rsb-accent/10"
        ].join(" "),

        destructive: [
          "bg-red-500/90 text-white shadow-lg shadow-red-500/20",
          "hover:bg-red-500 hover:shadow-red-500/30",
          "active:shadow-red-500/10"
        ].join(" "),

        success: [
          "bg-emerald-500/90 text-white shadow-lg shadow-emerald-500/20",
          "hover:bg-emerald-500 hover:shadow-emerald-500/30",
          "active:shadow-emerald-500/10"
        ].join(" "),

        outline: [
          "border border-white/10 bg-transparent text-rsb-text-secondary",
          "hover:bg-white/5 hover:text-white hover:border-white/20",
          "active:bg-white/10"
        ].join(" "),

        secondary: [
          "bg-white/5 text-rsb-text-secondary border border-white/5",
          "hover:bg-white/10 hover:text-white hover:border-white/10",
          "active:bg-white/15"
        ].join(" "),

        ghost: [
          "text-rsb-text-secondary",
          "hover:bg-white/5 hover:text-white",
          "active:bg-white/10"
        ].join(" "),

        link: [
          "text-rsb-accent underline-offset-4",
          "hover:underline hover:text-rsb-accent-glow",
          "active:scale-100" // No scale on link variant
        ].join(" "),

        glass: [
          "glass-panel text-white",
          "hover:bg-white/10 hover:border-rsb-accent/30",
          "active:bg-white/15",
          "shadow-glass"
        ].join(" "),

        glow: [
          "bg-rsb-accent text-white",
          "shadow-[0_0_20px_rgba(110,64,255,0.4)]",
          "hover:shadow-[0_0_30px_rgba(110,64,255,0.6)]",
          "active:shadow-[0_0_15px_rgba(110,64,255,0.3)]"
        ].join(" ")
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-xl px-6 text-base",
        xl: "h-14 rounded-2xl px-8 text-lg",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8 rounded-lg",
        "icon-lg": "h-12 w-12",
        // Mobile-friendly sizes (44px minimum touch target)
        "touch": "h-11 min-w-[44px] px-4",
        "touch-icon": "h-11 w-11"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          isLoading && "relative text-transparent cursor-wait"
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {/* Loading spinner overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        {/* Left icon */}
        {leftIcon && !isLoading && (
          <span className="flex-shrink-0">{leftIcon}</span>
        )}

        {/* Children */}
        {children}

        {/* Right icon */}
        {rightIcon && !isLoading && (
          <span className="flex-shrink-0">{rightIcon}</span>
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

// Loading spinner component
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-4 w-4 text-current"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

// Icon Button - optimized for icon-only buttons
export interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon'> {
  icon: React.ReactNode
  label: string // Required for accessibility
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, size = "icon", ...props }, ref) => {
    return (
      <Button
        ref={ref}
        size={size}
        aria-label={label}
        title={label}
        {...props}
      >
        {icon}
      </Button>
    )
  }
)
IconButton.displayName = "IconButton"

export { Button, IconButton, buttonVariants }
